import { useEffect, useRef, useSyncExternalStore } from "react";
import type { Hub } from "@foreman/app-core";
import { isAsk } from "./sessions-feed";

/**
 * While the app is open, a background push often never shows. So when something is waiting for you, the app itself says
 * so: a count in the tab title and on the home-screen icon, and a short buzz when a new one arrives.
 */
export function useAttention(hub: Hub): number {
  const waiting = useSyncExternalStore(hub.subscribe, hub.getInbox).filter((i) => isAsk(i.event)).length;
  const before = useRef(0);

  useEffect(() => {
    document.title = waiting > 0 ? `(${waiting}) Brocoded` : "Brocoded";
    const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
    try {
      void (waiting > 0 ? nav.setAppBadge?.(waiting) : nav.clearAppBadge?.())?.catch(() => undefined);
    } catch {
      /* no badge support: the title still shows it */
    }
  }, [waiting]);

  useEffect(() => {
    if (waiting > before.current && document.visibilityState === "visible") {
      try {
        navigator.vibrate?.([120, 80, 120]);
      } catch {
        /* not allowed before the first tap, or not supported */
      }
    }
    before.current = waiting;
  }, [waiting]);

  return waiting;
}
